import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  InputAdornment,
  OutlinedInput,
  Tooltip
} from '@mui/material';
import { CopyIcon, EyIcon, EyOffIcon } from '@components/icons/svg-icon.tsx';
import { copyToClipboard } from '@shared/utils/clipboard-utils.ts';
import useSnackbarStore from '@store/snackbar-store/snackbar.store.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import useOrganizationStore from '@modules/organization/store/organization.store.ts';
import OrganizationSectionCard from '@modules/organization/components/organization-section-card/organization-section-card.tsx';
import If from '@components/if/if.tsx';
import Loader from '@components/loader/loader.tsx';

const ApiKey: React.FC = () => {
  const { t } = useTranslation();
  const { token, getApiKey, isLoaded, isError } = useOrganizationStore();
  const { showSnackbar } = useSnackbarStore();
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const isPageLoading = !(isLoaded() || isError());

  const toggleKeyVisibility = () => setShowKey((prev) => !prev);

  const preventMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleCopy = async () => {
    if (!token) return;
    try {
      await copyToClipboard(token);
      setCopied(true);
      clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      showSnackbar(
        'settings_organization_api_key_copy_failed',
        SnackbarType.Error
      );
    }
  };

  useEffect(() => {
    getApiKey();
  }, []);

  return (
    <If
      condition={!isPageLoading}
      loading={isPageLoading}
      loadingContent={
        <div className="flex items-center justify-center h-[calc(100vh-260px)]">
          <Loader />
        </div>
      }
    >
      <OrganizationSectionCard
        title="settings_organization_api_key_title"
        description="settings_organization_api_key_description"
        className="max-w-[1200px]"
      >
        <OutlinedInput
          className="w-full"
          type={showKey ? 'text' : 'password'}
          value={token ?? ''}
          readOnly
          sx={{ fontFamily: 'monospace' }}
          endAdornment={
            <InputAdornment position="end" className="gap-1.5!">
              <IconButton
                onClick={toggleKeyVisibility}
                onMouseDown={preventMouseDown}
                edge="end"
                disabled={!token}
                aria-label={showKey ? 'hide api key' : 'show api key'}
              >
                {showKey ? (
                  <EyIcon stroke="var(--gray-2)" />
                ) : (
                  <EyOffIcon stroke="var(--gray-2)" />
                )}
              </IconButton>
              <Tooltip
                open={copied}
                title={t('settings_organization_api_key_copied')}
                placement="top"
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'var(--green-1)',
                      color: 'var(--white)',
                      fontWeight: 600,
                      '& .MuiTooltip-arrow': { color: 'var(--green-1)' }
                    }
                  }
                }}
              >
                <IconButton
                  onClick={handleCopy}
                  disabled={!token}
                  aria-label="copy api key"
                >
                  <CopyIcon stroke="var(--gray-2)" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          }
        />

        <div className="flex items-center gap-3.5 mt-6 rounded-lg border border-(--orange-2) bg-(--orange-1)/10 px-5 py-4 text-[15px] text-(--orange-1)/80">
          <span>{t('organization_api_key_warning')}</span>
        </div>
      </OrganizationSectionCard>
    </If>
  );
};

export default ApiKey;
